import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtINR, formatApiError, mediaUrl, customerAddressBook, formatAddressLine } from "@/lib/api";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Minus,
  X,
  ShoppingCart,
  UserPlus,
  UserCheck,
  Loader2,
  CalendarClock,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

function CartPanel({
  cart,
  mode,
  setMode,
  selectedCustomer,
  setSelectedCustomer,
  selectedAddressId,
  setSelectedAddressId,
  custSearch,
  setCustSearch,
  filteredCustomers,
  discount,
  setDiscount,
  taxRate,
  setTaxRate,
  shippingCharges,
  setShippingCharges,
  subtotal,
  tax,
  shipping,
  total,
  checkingOut,
  checkout,
  inc,
  dec,
  remove,
  isPreorder,
  setIsPreorder,
  expectedDate,
  setExpectedDate,
  dense = false,
}) {
  const pad = dense ? "p-4" : "p-6";
  return (
    <div className="flex flex-col h-full min-h-0 bg-black">
      <div className={`${pad} border-b border-zinc-800 shrink-0`}>
        <div className="flex items-center gap-2">
          <ShoppingCart className="h-4 w-4 text-[color:var(--mecha-red)]" />
          <div className="font-mono text-xs tracking-[0.3em] text-zinc-500">
            CART · {cart.length} ITEMS
          </div>
        </div>

        <div className="mt-4 flex border border-zinc-800">
          <button
            data-testid="mode-new"
            type="button"
            onClick={() => {
              setMode("new");
              setSelectedCustomer(null);
              setSelectedAddressId("");
            }}
            className={`flex-1 py-2 text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2 ${
              mode === "new" ? "bg-white text-black" : "text-zinc-500"
            }`}
          >
            <UserPlus className="h-3 w-3" /> New
          </button>
          <button
            data-testid="mode-returning"
            type="button"
            onClick={() => setMode("returning")}
            className={`flex-1 py-2 text-xs font-mono tracking-widest uppercase flex items-center justify-center gap-2 ${
              mode === "returning" ? "bg-white text-black" : "text-zinc-500"
            }`}
          >
            <UserCheck className="h-3 w-3" /> Returning
          </button>
        </div>

        <div className="mt-3 flex border border-zinc-800">
          <button
            data-testid="mode-sale"
            type="button"
            onClick={() => setIsPreorder(false)}
            className={`flex-1 py-2 text-xs font-mono tracking-widest uppercase ${
              !isPreorder ? "bg-white text-black" : "text-zinc-500"
            }`}
          >
            Sale
          </button>
          <button
            data-testid="mode-preorder"
            type="button"
            onClick={() => setIsPreorder(true)}
            className={`flex-1 py-2 text-xs font-mono tracking-widest uppercase ${
              isPreorder ? "bg-[color:var(--mecha-red)] text-white" : "text-zinc-500"
            }`}
          >
            Pre-order
          </button>
        </div>

        {mode === "returning" && (
          <div className="mt-3">
            {selectedCustomer ? (
              <>
              <div
                data-testid="selected-customer"
                className="border border-[color:var(--neon-cyan)] p-3 flex items-center justify-between"
              >
                <div>
                  <div className="text-sm font-medium">{selectedCustomer.name}</div>
                  <div className="font-mono text-[10px] text-zinc-400">
                    {selectedCustomer.phone}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCustomer(null);
                    setSelectedAddressId("");
                  }}
                  className="text-zinc-500 hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              {customerAddressBook(selectedCustomer).length > 0 && (
                <div className="mt-2 border border-zinc-800 p-3 space-y-2" data-testid="pos-address-picker">
                  <div className="font-mono text-[9px] tracking-[0.3em] text-zinc-500">
                    SHIP TO
                  </div>
                  {customerAddressBook(selectedCustomer).map((addr) => (
                    <label
                      key={addr.id}
                      className={`block text-left p-2 border cursor-pointer ${
                        selectedAddressId === addr.id
                          ? "border-[color:var(--neon-cyan)] bg-zinc-900"
                          : "border-zinc-800 hover:border-zinc-600"
                      }`}
                    >
                      <input
                        type="radio"
                        name="pos-ship-address"
                        className="mr-2"
                        checked={selectedAddressId === addr.id}
                        onChange={() => setSelectedAddressId(addr.id)}
                      />
                      <span className="text-xs font-medium">
                        {addr.label || "Address"}
                        {addr.is_default ? " · default" : ""}
                      </span>
                      <div className="text-[11px] text-zinc-400 mt-1 pl-6 leading-relaxed">
                        {formatAddressLine(addr)}
                      </div>
                    </label>
                  ))}
                </div>
              )}
              </>
            ) : (
              <>
                <input
                  data-testid="cust-search"
                  value={custSearch}
                  onChange={(e) => setCustSearch(e.target.value)}
                  placeholder="Search name / phone"
                  className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[color:var(--mecha-red)]"
                />
                {custSearch && (
                  <div className="max-h-40 overflow-y-auto border border-zinc-800 border-t-0">
                    {filteredCustomers.slice(0, 5).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        data-testid={`cust-option-${c.phone}`}
                        onClick={() => {
                          setSelectedCustomer(c);
                          const book = customerAddressBook(c);
                          const pick = book.find((a) => a.is_default) || book[0];
                          setSelectedAddressId(pick?.id || "");
                          setCustSearch("");
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-zinc-900 border-b border-zinc-900 last:border-0"
                      >
                        <div className="text-sm">{c.name}</div>
                        <div className="font-mono text-[10px] text-zinc-500">
                          {c.phone}
                        </div>
                      </button>
                    ))}
                    {filteredCustomers.length === 0 && (
                      <div className="px-3 py-2 text-xs text-zinc-500">No match found</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {cart.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">
            {isPreorder ? "Add products to book a pre-order." : "Add products to start a sale."}
          </div>
        ) : (
          <div className="divide-y divide-zinc-900">
            {cart.map((i) => (
              <div
                key={i.product_id}
                className="p-4 flex items-start gap-3"
                data-testid={`cart-item-${i.sku}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{i.name}</div>
                  <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                    {i.sku} · {fmtINR(i.price)}
                    {isPreorder && i._stock <= 0 ? " · PRE-ORDER" : ""}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => dec(i.product_id)}
                      className="w-7 h-7 border border-zinc-800 flex items-center justify-center hover:border-white"
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <div className="w-8 text-center font-mono">{i.quantity}</div>
                    <button
                      type="button"
                      onClick={() => inc(i.product_id)}
                      className="w-7 h-7 border border-zinc-800 flex items-center justify-center hover:border-white"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold">
                    {fmtINR(i.price * i.quantity)}
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(i.product_id)}
                    className="mt-2 text-zinc-500 hover:text-[color:var(--mecha-red)]"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={`border-t border-zinc-800 ${pad} space-y-4 shrink-0`}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block font-mono text-[9px] tracking-[0.3em] text-zinc-500 mb-1">
              DISCOUNT
            </label>
            <input
              data-testid="pos-discount"
              type="number"
              min="0"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-sm focus:outline-none focus:border-[color:var(--mecha-red)]"
            />
          </div>
          <div>
            <label className="block font-mono text-[9px] tracking-[0.3em] text-zinc-500 mb-1">
              TAX %
            </label>
            <input
              data-testid="pos-tax"
              type="number"
              min="0"
              value={taxRate}
              onChange={(e) => setTaxRate(e.target.value)}
              className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-sm focus:outline-none focus:border-[color:var(--mecha-red)]"
            />
          </div>
          <div>
            <label className="block font-mono text-[9px] tracking-[0.3em] text-zinc-500 mb-1">
              SHIPPING
            </label>
            <input
              data-testid="pos-shipping"
              type="number"
              min="0"
              step="0.01"
              value={shippingCharges}
              onChange={(e) => setShippingCharges(e.target.value)}
              className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-sm focus:outline-none focus:border-[color:var(--mecha-red)]"
            />
          </div>
        </div>

        <div className="space-y-1 text-sm">
          <div className="flex justify-between text-zinc-400">
            <span>Subtotal</span>
            <span className="font-mono">{fmtINR(subtotal)}</span>
          </div>
          <div className="flex justify-between text-zinc-400">
            <span>Discount</span>
            <span className="font-mono">- {fmtINR(discount)}</span>
          </div>
          <div className="flex justify-between text-zinc-400">
            <span>Tax</span>
            <span className="font-mono">{fmtINR(tax)}</span>
          </div>
          <div className="flex justify-between text-zinc-400">
            <span>Shipping</span>
            <span className="font-mono" data-testid="pos-shipping-line">
              {fmtINR(shipping)}
            </span>
          </div>
        </div>

        {isPreorder && (
          <div>
            <label className="block font-mono text-[9px] tracking-[0.3em] text-zinc-500 mb-1">
              EXPECTED DATE (OPTIONAL)
            </label>
            <div className="relative">
              <CalendarClock className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
              <input
                data-testid="pos-expected-date"
                type="date"
                value={expectedDate}
                onChange={(e) => setExpectedDate(e.target.value)}
                className="w-full h-10 bg-zinc-950 border border-zinc-800 pl-9 pr-3 text-sm text-white focus:outline-none focus:border-[color:var(--mecha-red)]"
              />
            </div>
            <div className="mt-1.5 font-mono text-[9px] tracking-widest text-zinc-500">
              Stock is not deducted until you fulfill this order.
            </div>
          </div>
        )}

        <div className="flex justify-between items-end border-t border-zinc-800 pt-4">
          <span className="font-mono text-xs tracking-[0.3em] text-zinc-500">TOTAL</span>
          <span
            className="font-display text-3xl text-[color:var(--mecha-red)]"
            data-testid="pos-total"
          >
            {fmtINR(total)}
          </span>
        </div>

        <button
          data-testid="pos-checkout-btn"
          type="button"
          disabled={cart.length === 0 || checkingOut}
          onClick={checkout}
          className="btn-brutal w-full h-14 bg-[color:var(--mecha-red)] text-white font-bold tracking-widest uppercase text-sm hover:bg-[color:var(--mecha-red-hover)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {checkingOut ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isPreorder ? (
            "Book Pre-order (Cash)"
          ) : (
            "Complete Sale (Cash)"
          )}
        </button>
      </div>
    </div>
  );
}

export default function POS() {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  const [shippingCharges, setShippingCharges] = useState(0);
  const [mode, setMode] = useState("new");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [custSearch, setCustSearch] = useState("");
  const [checkingOut, setCheckingOut] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isPreorder, setIsPreorderState] = useState(false);
  const [expectedDate, setExpectedDate] = useState("");
  const nav = useNavigate();

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/api/products"), api.get("/api/customers")]);
    setProducts(p.data);
    setCustomers(c.data);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const apply = () => setIsDesktop(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return products;
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        p.sku.toLowerCase().includes(term) ||
        p.category?.toLowerCase().includes(term)
    );
  }, [products, q]);

  const filteredCustomers = useMemo(() => {
    const term = custSearch.trim().toLowerCase();
    if (!term) return customers;
    return customers.filter(
      (c) => c.name.toLowerCase().includes(term) || c.phone.includes(term)
    );
  }, [customers, custSearch]);

  const setIsPreorder = (next) => {
    if (!next && isPreorder) {
      setCart((prev) => {
        const nextCart = prev
          .filter((i) => i._stock > 0)
          .map((i) => (i.quantity > i._stock ? { ...i, quantity: i._stock } : i));
        if (nextCart.length !== prev.length) {
          toast.error("Removed out-of-stock items — they need Pre-order mode");
        } else if (nextCart.some((i, idx) => i.quantity !== prev[idx].quantity)) {
          toast.error("Quantities capped to available stock");
        }
        return nextCart;
      });
      setExpectedDate("");
    }
    setIsPreorderState(next);
  };

  const addToCart = (p) => {
    if (!isPreorder && p.stock <= 0) return toast.error("Out of stock");
    setCart((prev) => {
      const existing = prev.find((i) => i.product_id === p.id);
      if (existing) {
        if (!isPreorder && existing.quantity >= p.stock) {
          toast.error(`Only ${p.stock} in stock`);
          return prev;
        }
        return prev.map((i) =>
          i.product_id === p.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [
        ...prev,
        {
          product_id: p.id,
          name: p.name,
          sku: p.sku,
          price: p.price,
          quantity: 1,
          _stock: p.stock,
        },
      ];
    });
  };

  const inc = (id) =>
    setCart((prev) =>
      prev.map((i) => {
        if (i.product_id !== id) return i;
        if (!isPreorder && i.quantity >= i._stock) {
          toast.error(`Only ${i._stock} in stock`);
          return i;
        }
        return { ...i, quantity: i.quantity + 1 };
      })
    );
  const dec = (id) =>
    setCart((prev) =>
      prev
        .map((i) => (i.product_id === id ? { ...i, quantity: i.quantity - 1 } : i))
        .filter((i) => i.quantity > 0)
    );
  const remove = (id) => setCart((prev) => prev.filter((i) => i.product_id !== id));

  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const tax = (subtotal * (taxRate || 0)) / 100;
  const shipping = Math.max(0, parseFloat(shippingCharges) || 0);
  const total = Math.max(0, subtotal - (discount || 0) + tax + shipping);
  const cartQty = cart.reduce((s, i) => s + i.quantity, 0);

  const checkout = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    if (mode === "returning" && !selectedCustomer)
      return toast.error("Select a returning customer");
    setCheckingOut(true);
    try {
      const { data } = await api.post("/api/orders/checkout", {
        items: cart.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          sku: i.sku,
          price: i.price,
          quantity: i.quantity,
        })),
        discount: parseFloat(discount) || 0,
        tax_rate: parseFloat(taxRate) || 0,
        shipping_charges: shipping,
        payment_method: "cash",
        customer_id: mode === "returning" ? selectedCustomer.id : null,
        address_id: mode === "returning" && selectedAddressId && selectedAddressId !== "legacy" ? selectedAddressId : null,
        is_preorder: isPreorder,
        expected_date: isPreorder && expectedDate ? expectedDate : null,
      });
      toast.success(
        isPreorder
          ? `Pre-order ${data.order_number} booked`
          : `Order ${data.order_number} created`
      );
      setCart([]);
      setDiscount(0);
      setShippingCharges(0);
      setSelectedCustomer(null);
      setSelectedAddressId("");
      setExpectedDate("");
      setCartOpen(false);
      nav(`/orders/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setCheckingOut(false);
    }
  };

  const cartProps = {
    cart,
    mode,
    setMode,
    selectedCustomer,
    setSelectedCustomer,
    selectedAddressId,
    setSelectedAddressId,
    custSearch,
    setCustSearch,
    filteredCustomers,
    discount,
    setDiscount,
    taxRate,
    setTaxRate,
    shippingCharges,
    setShippingCharges,
    subtotal,
    tax,
    shipping,
    total,
    checkingOut,
    checkout,
    inc,
    dec,
    remove,
    isPreorder,
    setIsPreorder,
    expectedDate,
    setExpectedDate,
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] min-h-[calc(100dvh-3.5rem)] lg:min-h-screen pb-20 lg:pb-0">
      <div className="p-4 sm:p-6 lg:p-10 overflow-y-auto">
        <div className="mb-6">
          <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)]">
            // POINT OF SALE
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mt-2 leading-none">
            {isPreorder ? "PRE-ORDER" : "CHECKOUT"}
          </h1>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <input
            data-testid="pos-product-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products, SKU, category..."
            className="w-full h-12 bg-zinc-950 border border-zinc-800 pl-12 pr-4 text-white placeholder-zinc-600 focus:outline-none focus:border-[color:var(--mecha-red)]"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="border border-zinc-800 p-8 sm:p-12 text-center">
            <div className="text-zinc-500 mb-4">No products found.</div>
            <button
              type="button"
              onClick={() => nav("/products")}
              className="btn-brutal border border-white text-white px-6 h-11 text-xs font-mono tracking-widest uppercase hover:bg-white hover:text-black"
            >
              Add Products →
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`pos-product-${p.sku}`}
                onClick={() => addToCart(p)}
                disabled={!isPreorder && p.stock <= 0}
                className="text-left card-brutal hoverable-card p-3 sm:p-4 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <div className="aspect-square bg-zinc-900 mb-3 overflow-hidden">
                  {p.image_url ? (
                    <img
                      src={mediaUrl(p.image_url)}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-700 font-display text-4xl">
                      {p.name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="font-mono text-[10px] tracking-widest text-zinc-500 truncate">
                  {p.sku}
                </div>
                <div className="font-medium text-sm mt-1 line-clamp-2 min-h-[2.5rem]">
                  {p.name}
                </div>
                <div className="flex items-end justify-between mt-3 gap-2">
                  <div className="font-mono font-bold text-[color:var(--mecha-red)] text-sm sm:text-base">
                    {fmtINR(p.price)}
                  </div>
                  <div
                    className={`font-mono text-[10px] tracking-widest shrink-0 ${
                      p.stock <= 0
                        ? "text-[color:var(--mecha-red)]"
                        : p.stock <= 3
                          ? "text-[color:var(--warn)]"
                          : "text-zinc-500"
                    }`}
                  >
                    {p.stock <= 0
                      ? isPreorder
                        ? "PRE-ORDER"
                        : "SOLD OUT"
                      : `${p.stock} LEFT`}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Desktop cart */}
      {isDesktop && (
        <aside className="border-l border-zinc-800 bg-black flex flex-col sticky top-0 h-screen">
          <CartPanel {...cartProps} />
        </aside>
      )}

      {/* Mobile cart bar */}
      {!isDesktop && (
        <div className="fixed bottom-0 inset-x-0 z-30 border-t border-zinc-800 bg-black/95 backdrop-blur p-3 safe-bottom">
          <button
            type="button"
            data-testid="mobile-cart-btn"
            onClick={() => setCartOpen(true)}
            className="btn-brutal w-full h-12 bg-[color:var(--mecha-red)] text-white text-xs font-mono tracking-widest uppercase flex items-center justify-between px-4"
          >
            <span className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Cart · {cartQty}
            </span>
            <span className="font-display text-base normal-case tracking-normal">
              {fmtINR(total)}
            </span>
          </button>
        </div>
      )}

      {!isDesktop && (
        <Sheet open={cartOpen} onOpenChange={setCartOpen}>
          <SheetContent
            side="bottom"
            className="h-[min(92dvh,720px)] p-0 bg-black border-zinc-800 flex flex-col gap-0"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Cart</SheetTitle>
            </SheetHeader>
            <CartPanel {...cartProps} dense />
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
